import { FC } from 'react';
import { BLOCKS, MARKS } from '@contentful/rich-text-types';
import cn from 'classnames';

import classes from 'src/styles/_commonClasses.module.scss';
import type { Block } from 'src/__generated__/graphqlTypes';
import type { RichTextParsersConfig } from 'types/objectTypes';
import type { SectionComponentInterface } from 'types/componentTypes';
import {
  Content,
  DataLink,
  Title,
} from 'components/Shared/SharedComponents';
import { CommonSize, ElementAlign } from 'constants/enums';
import { capitalizeFirstLetter } from 'lib/text.service';

// @ts-ignore TS doesn't like buttonHeight, buttonMargin
import styles, { buttonHeight, buttonMargin } from './PlainItemsWithDescription.module.scss';
import { PlainItems } from './PlainItems';

import { PlainItemWithDescriptionButton as ButtonInterface, PlainItemWithDescriptionRichTextParam } from '../config/declarations';

const getParsersConfig = ({
  titleAlign,
  pAlign,
}: PlainItemWithDescriptionRichTextParam): RichTextParsersConfig => {
  const getHClasses = (heading: 'H1' | 'H2' | 'H3', specificClass?: string) => cn(
    styles.title,
    classes[`titleAlign${capitalizeFirstLetter(titleAlign)}`],
    {
      [specificClass || classes[`titleMain${heading}`]]: !styles[`plainItemsTitle${heading}`],
    },
  );
  const paragraphAlign = classes[`pAlign${capitalizeFirstLetter(pAlign)}`];

  return ({
    [BLOCKS.HEADING_1]: {
      classNames: getHClasses('H1'),
    },
    [BLOCKS.HEADING_2]: {
      classNames: getHClasses('H2'),
    },
    [BLOCKS.HEADING_3]: {
      classNames: getHClasses('H3'),
    },
    [BLOCKS.PARAGRAPH]: {
      classNames: cn(styles.paragraph, paragraphAlign),
    },
    [MARKS.BOLD]: {
      classNames: classes.textBold,
    },
  });
};

/* Nested component */
const Button: FC<ButtonInterface> = ({ button, isOutside = false }) => {
  if (!button) {
    return null;
  }

  return (
    <DataLink
      link={button}
      className={cn(styles.button, isOutside && styles.buttonOutside)}
    >
      {button.title}
    </DataLink>
  );
};

/* Main Component */
const PlainItemsWithDescription: FC<SectionComponentInterface> = ({
  blocks,
  extraData,
  bgColor,
}) => {
  const [block] = blocks;

  if (!block) {
    return null;
  }

  const {
    titleAlign = ElementAlign.CENTER,
    pAlign = ElementAlign.CENTER,
    verticalMargin = CommonSize.X_SMALL,
    isOutsideButton = false,
  } = extraData || {};
  const {
    content,
    richTitle,
    link,
    contentTypesCollection,
  } = block;
  const links = contentTypesCollection?.items as Array<Block>;
  const richTextConfig = getParsersConfig({ titleAlign, pAlign });

  return (
    <section
      className={cn(styles.wrapper, classes[`marginVertical${capitalizeFirstLetter(verticalMargin)}`])}
      style={{
        background: bgColor,
        ...(isOutsideButton && buttonHeight
          && { paddingBottom: `${Number(buttonHeight) + Number(buttonMargin) || 0}px` }),
      }}
    >
      <div className={cn(styles.plainItems)}>
        <Title richText={richTitle} config={richTextConfig} />
        <Content richText={content} config={richTextConfig} />
        <PlainItems blocks={links} />
        <Button button={link} isOutside={isOutsideButton} />
      </div>
    </section>
  );
};

export default PlainItemsWithDescription;
